import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { CheckCircle, Factory, Award, Truck, Shield, Phone, Zap, Package, Users, Headphones, Cable, Battery } from 'lucide-react';
import { Button } from '@/components/ui/button';

const MobileAccessoriesManufacturerIndia = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const productCategories = [
    { icon: Cable, name: 'Data Cables', desc: 'USB Type-C, Micro USB, Lightning cables. 45W-120W fast charging.', link: '/shop/data-cables' },
    { icon: Battery, name: 'Mobile Chargers', desc: '15W-65W BIS certified chargers. QC, PD, VOOC support.', link: '/shop/mobile-chargers' },
    { icon: Zap, name: 'Car Chargers', desc: '18W-65W dual port car chargers. QC+PD technology.', link: '/shop/car-chargers' },
    { icon: Headphones, name: 'TWS Earbuds', desc: 'Bluetooth 5.3, ANC, 30+ hours battery.', link: '/shop/earbuds' },
    { icon: Headphones, name: 'Neckbands', desc: 'Magnetic design, 20+ hours playback, IPX5.', link: '/shop/neckbands' },
    { icon: Headphones, name: 'Wired Earphones', desc: 'Type-C & 3.5mm, in-line mic, premium sound.', link: '/shop/wired-handfree' }
  ];

  const whyRSD = [
    { title: '8+ Years Experience', desc: 'Trusted manufacturing since 2016' },
    { title: 'ISO 9001:2015', desc: 'International quality certification' },
    { title: '50,000+ Daily Production', desc: 'High capacity manufacturing' },
    { title: '500+ Dealer Network', desc: 'Pan India distribution reach' },
    { title: 'BIS Certified Products', desc: 'Bureau of Indian Standards compliant' },
    { title: 'OEM/ODM Services', desc: 'Private label manufacturing' },
    { title: 'Made in India', desc: 'Atmanirbhar Bharat initiative' },
    { title: '1 Year Warranty', desc: 'Quality assurance guarantee' }
  ];

  const faqs = [
    {
      q: 'Who is the best mobile accessories manufacturer in India?',
      a: 'RSD Accessories Pvt Ltd is one of India\'s leading mobile accessories manufacturers, based in Noida, Delhi NCR. With 8+ years of experience, ISO 9001:2015 certification, and 50,000+ daily production capacity, RSD manufactures data cables, chargers, car chargers, earbuds, and neckbands. Their 500+ dealer network serves distributors across all states of India.'
    },
    {
      q: 'What mobile accessories does RSD manufacture?',
      a: 'RSD manufactures a complete range of mobile accessories: Data Cables (USB to Type-C, Micro USB, Lightning, 45W-120W), Mobile Chargers (15W-65W, BIS certified), Car Chargers (18W-65W), TWS Earbuds (ANC, Bluetooth 5.3), Neckbands (magnetic, 20+ hours), and Wired Earphones (Type-C, 3.5mm). All products are quality tested and come with warranty.'
    },
    {
      q: 'Does RSD provide wholesale mobile accessories?',
      a: 'Yes, RSD is exclusively a B2B wholesale manufacturer. We serve distributors, wholesalers, e-commerce sellers (Amazon/Flipkart/Meesho), retail chains, and corporate gifting companies. Minimum Order Quantity (MOQ) applies, typically starting from 5,000-10,000 units depending on the product category.'
    },
    {
      q: 'Does RSD offer OEM mobile accessories manufacturing?',
      a: 'Yes, RSD provides comprehensive OEM and ODM services for all mobile accessories. Services include: custom branding/logo printing, custom colors and designs, private label packaging, exclusive product development, and white label solutions. Over 100 brands trust RSD for their OEM needs.'
    },
    {
      q: 'What is the quality standard of RSD products?',
      a: 'RSD maintains strict quality standards: ISO 9001:2015 certified facility, BIS certification for chargers, 100% product testing, 10,000+ bend cycle testing for cables, electrical safety testing, and dedicated quality control team. All products come with 1-year warranty.'
    },
    {
      q: 'Does RSD supply mobile accessories across India?',
      a: 'Yes, RSD provides Pan India wholesale supply. From our Noida factory, we serve all 28 states and 8 union territories. Major cities served include Delhi, Mumbai, Bangalore, Chennai, Kolkata, Hyderabad, Ahmedabad, Pune, and all tier-2/tier-3 cities. Delivery within 3-5 business days.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Mobile Accessories Manufacturer in India | RSD Accessories - Complete Range</title>
        <meta name="description" content="RSD Accessories - India's leading mobile accessories manufacturer. Data cables, chargers, earbuds, neckbands. ISO certified, 50,000+ daily production. OEM manufacturing. Wholesale prices." />
        <meta name="keywords" content="mobile accessories manufacturer india, mobile accessories wholesale, data cable manufacturer, charger manufacturer, earbuds manufacturer, neckband manufacturer, oem mobile accessories, bulk accessories supplier" />
        <link rel="canonical" href="https://rsdaccessories.com/mobile-accessories-manufacturer-india" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": { "@type": "Answer", "text": faq.a }
            }))
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-neutral-900 via-neutral-800 to-black py-20 md:py-28">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23FF4D00" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center">
              <span className="inline-block bg-[#FF4D00] text-white px-4 py-1 rounded-full text-sm font-semibold mb-6">
                Complete Mobile Accessories Range
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6">
                Mobile Accessories Manufacturer in <span className="text-[#FF4D00]">India</span>
              </h1>
              <p className="text-xl md:text-2xl text-neutral-300 max-w-3xl mx-auto mb-8">
                RSD Accessories - ISO Certified Factory | Cables, Chargers, Earbuds, Neckbands | 50,000+ Daily Production
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="bg-[#FF4D00] hover:bg-[#E64500] text-white px-8 py-6 text-lg">
                    <Phone className="w-5 h-5 mr-2" />
                    Request Bulk Quote
                  </Button>
                </Link>
                <Link to="/shop">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-black px-8 py-6 text-lg">
                    View All Products
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Product Categories */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
                Complete Mobile Accessories Range
              </h2>
              <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
                From cables to audio - we manufacture everything your business needs
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {productCategories.map((category, idx) => (
                <Link key={idx} to={category.link} className="group">
                  <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all border border-neutral-100 hover:border-[#FF4D00] h-full">
                    <div className="w-14 h-14 bg-[#FF4D00]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#FF4D00] transition-colors">
                      <category.icon className="w-7 h-7 text-[#FF4D00] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-xl font-bold text-neutral-900 mb-2">{category.name}</h3>
                    <p className="text-neutral-600">{category.desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Why RSD Section */}
        <section className="py-16 bg-neutral-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Why RSD is India's Preferred Manufacturer
              </h2>
              <p className="text-neutral-400 max-w-2xl mx-auto">
                Trusted by 500+ dealers and 100+ brands for quality mobile accessories
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whyRSD.map((item, idx) => (
                <div key={idx} className="bg-neutral-800 rounded-xl p-6 text-center hover:bg-neutral-700 transition-colors">
                  <CheckCircle className="w-10 h-10 text-[#FF4D00] mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                  <p className="text-neutral-400 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* B2B Services */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-6">
                  B2B Services We Offer
                </h2>
                <p className="text-lg text-neutral-600 mb-8">
                  RSD Accessories is your complete partner for mobile accessories business. Whether you're a distributor, e-commerce seller, or launching your own brand - we have solutions.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-neutral-50 rounded-lg">
                    <Package className="w-8 h-8 text-[#FF4D00] flex-shrink-0" />
                    <div>
                      <h3 className="font-bold text-neutral-900">Wholesale Distribution</h3>
                      <p className="text-neutral-600 text-sm">Bulk quantities at competitive B2B prices. MOQ from 5,000 units.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 bg-neutral-50 rounded-lg">
                    <Factory className="w-8 h-8 text-[#FF4D00] flex-shrink-0" />
                    <div>
                      <h3 className="font-bold text-neutral-900">OEM Manufacturing</h3>
                      <p className="text-neutral-600 text-sm">Custom branding, logo printing, private label solutions.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 bg-neutral-50 rounded-lg">
                    <Award className="w-8 h-8 text-[#FF4D00] flex-shrink-0" />
                    <div>
                      <h3 className="font-bold text-neutral-900">ODM Services</h3>
                      <p className="text-neutral-600 text-sm">Custom product development, exclusive designs, tooling support.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 bg-neutral-50 rounded-lg">
                    <Truck className="w-8 h-8 text-[#FF4D00] flex-shrink-0" />
                    <div>
                      <h3 className="font-bold text-neutral-900">Pan India Delivery</h3>
                      <p className="text-neutral-600 text-sm">Fast shipping to all states. 3-5 business days delivery.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-[#FF4D00] to-orange-600 rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-6">Target Customers</h3>
                <ul className="space-y-4">
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6" />
                    <span>Mobile Accessories Distributors</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6" />
                    <span>Wholesale Dealers</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6" />
                    <span>E-commerce Sellers (Amazon, Flipkart, Meesho)</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6" />
                    <span>Retail Chain Buyers</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6" />
                    <span>Corporate Gifting Companies</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6" />
                    <span>Private Label Brand Owners</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6" />
                    <span>Export Clients</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-neutral-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
                Frequently Asked Questions
              </h2>
            </div>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-white rounded-xl p-6 shadow border border-neutral-200">
                  <h3 className="text-lg font-bold text-neutral-900 mb-3">{faq.q}</h3>
                  <p className="text-neutral-600">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-[#FF4D00]">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Start Your Mobile Accessories Business Today
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Partner with India's trusted manufacturer - Quality products, competitive prices, reliable delivery
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-[#FF4D00] hover:bg-neutral-100 px-8 py-6 text-lg">
                  <Phone className="w-5 h-5 mr-2" />
                  Call: +91-9311211889
                </Button>
              </Link>
              <Link to="/become-distributor">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#FF4D00] px-8 py-6 text-lg">
                  Become a Distributor
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default MobileAccessoriesManufacturerIndia;
